import { useEffect, useRef, type RefObject } from "react";

type Modal = { node: HTMLElement; close: () => void; previous: HTMLElement | null };
const stack: Modal[] = [];
const initialInert = new Map<HTMLElement, boolean>();
let initialOverflow = "";
const focusable =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function candidates(node: HTMLElement) {
  return [...node.querySelectorAll<HTMLElement>(focusable)].filter(
    (el) => el.getClientRects().length > 0 && !el.closest('[inert],[aria-hidden="true"]'),
  );
}
function syncBackground() {
  const top = stack.at(-1)?.node;
  for (const child of document.body.children) {
    if (!(child instanceof HTMLElement)) continue;
    if (!initialInert.has(child)) initialInert.set(child, child.inert);
    child.inert = top ? !child.contains(top) : (initialInert.get(child) ?? false);
  }
  if (!top) {
    for (const [el, inert] of initialInert) el.inert = inert;
    initialInert.clear();
  }
}

/** Modales portées dans body : focus initial, boucle Tab, fond inerte et retour au déclencheur. */
export function useModalAccessibility(
  ref: RefObject<HTMLElement>,
  open: boolean,
  onClose: () => void,
  blocking = false,
) {
  const latest = useRef({ onClose, blocking });
  latest.current = { onClose, blocking };
  useEffect(() => {
    const node = ref.current;
    if (!open || !node) return;
    const modal: Modal = {
      node,
      close: () => latest.current.onClose(),
      previous: document.activeElement as HTMLElement | null,
    };
    if (!stack.length) initialOverflow = document.body.style.overflow;
    stack.push(modal);
    document.body.style.overflow = "hidden";
    syncBackground();
    (candidates(node)[0] ?? node).focus();
    const keydown = (e: KeyboardEvent) => {
      if (stack.at(-1) !== modal) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!latest.current.blocking) modal.close();
      }
      if (e.key === "Tab") {
        const all = candidates(node);
        const first = all[0] ?? node;
        const last = all.at(-1) ?? node;
        if (
          !all.length ||
          !node.contains(document.activeElement) ||
          (e.shiftKey && document.activeElement === first)
        ) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    const focusin = (e: FocusEvent) => {
      if (stack.at(-1) === modal && !node.contains(e.target as Node))
        (candidates(node)[0] ?? node).focus();
    };
    document.addEventListener("keydown", keydown);
    document.addEventListener("focusin", focusin);
    return () => {
      document.removeEventListener("keydown", keydown);
      document.removeEventListener("focusin", focusin);
      stack.splice(stack.indexOf(modal), 1);
      syncBackground();
      if (!stack.length) document.body.style.overflow = initialOverflow;
      if (modal.previous?.isConnected && !modal.previous.closest("[inert]")) modal.previous.focus();
    };
  }, [open, ref]);
}
